import { useState, useEffect } from 'react';
import {
    Box,
    Container,
    VStack,
    Heading,
    Text,
    IconButton,
    Flex,
    Badge,
    HStack,
    Spinner,
    Alert,
    AlertIcon,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { domainName } from '../variables';

const PropertiesSlideshow = () => {
    const { t } = useTranslation();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Fetch properties from your Django API
        const fetchProperties = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${domainName}/services/`); // Update with your actual API endpoint
                
                if (!response.ok) {
                    throw new Error('Failed to fetch properties');
                }
                
                const data = await response.json();
                
                // Filter only active properties
                const activeProperties = data.filter(property => property.isActive);
                
                setProperties(activeProperties);
                setError(null);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchProperties();
    }, []);

    useEffect(() => {
        if (properties.length === 0) return;
        
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % properties.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [properties.length]);

    const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % properties.length);
    const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + properties.length) % properties.length);

    // Helper function to format price based on currency and type
    const formatPrice = (price, currency, type) => {
        const currencySymbols = {
            mad: 'MAD',
            usd: '$',
            eur: '€'
        };
        
        const formattedPrice = new Intl.NumberFormat('fr-MA').format(price);
        const symbol = currencySymbols[currency?.toLowerCase()] || currency?.toUpperCase();
        
        if (type === 'Location') {
            return `${formattedPrice} ${symbol}/mois`;
        }
        return `${formattedPrice} ${symbol}`;
    };

    // Helper function to get a random image or placeholder
    const getPropertyImage = (property) => {
        if (property.images && property.images.length > 0) {
            const randomIndex = Math.floor(Math.random() * property.images.length);
            return property.images[randomIndex].image;
        }
        return 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&auto=format&fit=crop';
    };

    if (loading) {
        return (
            <Box as="section" py={20}>
                <Container maxW="container.xl">
                    <Flex justify="center" align="center" minH="400px">
                        <Spinner size="xl" color="cyan.500" thickness="4px" />
                    </Flex>
                </Container>
            </Box>
        );
    }

    if (error) {
        return (
            <Box as="section" py={20}>
                <Container maxW="container.xl">
                    <Alert status="error" borderRadius="lg">
                        <AlertIcon />
                        {t("Erreur lors du chargement des biens")}: {error}
                    </Alert>
                </Container>
            </Box>
        );
    }

    if (properties.length === 0) {
        return null;
    }

    return (
        <Box as="section" py={20}>
            <Container maxW="container.xl">
                <VStack spacing={10} mb={16}>
                    <Heading
                        as="h2"
                        size="xl"
                        textAlign="center"
                        bgGradient="linear(to-r, cyan.600, blue.600)"
                        bgClip="text"
                    >
                        {t("Biens Immobiliers en Vedette")}
                    </Heading>
                    <Text
                        fontSize="lg"
                        color="gray.600"
                        textAlign="center"
                        maxW="2xl"
                    >
                        {t("Découvrez notre sélection de biens d'exception")}
                    </Text>
                </VStack>

                <Box position="relative">
                    <Box
                        overflow="hidden"
                        borderRadius="3xl"
                        boxShadow="2xl"
                        position="relative"
                    >
                        <Box position="relative" h={{ base: "300px", md: "380px" }}>
                            {properties.map((property, index) => (
                                <Box
                                    key={property.id}
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    w="100%"
                                    h="100%"
                                    opacity={index === currentSlide ? 1 : 0}
                                    transform={index === currentSlide ? "scale(1)" : "scale(0.95)"}
                                    transition="all 0.7s ease-in-out"
                                >
                                    <Box
                                        as="img"
                                        src={getPropertyImage(property)}
                                        alt={property.title}
                                        w="100%"
                                        h="100%"
                                        objectFit="cover"
                                    />
                                    <Box
                                        position="absolute"
                                        top={0}
                                        left={0}
                                        right={0}
                                        bottom={0}
                                        bgGradient="linear(to-t, blackAlpha.800, blackAlpha.400, transparent)"
                                    >
                                        <Box
                                            position="absolute"
                                            bottom={0}
                                            left={10}
                                            right={0}
                                            p={{ base: 4, md: 6 }}
                                            color="white"
                                        >
                                            <Box maxW={{ base: "100%", md: "2xl" }}>
                                                <HStack spacing={2} mb={2}>
                                                    <Badge
                                                        colorScheme="cyan"
                                                        px={3}
                                                        py={1}
                                                        borderRadius="full"
                                                        fontSize="sm"
                                                        fontWeight="bold"
                                                    >
                                                        {property.type}
                                                    </Badge>
                                                    {property.category && (
                                                        <Badge
                                                            colorScheme="blue"
                                                            px={3}
                                                            py={1}
                                                            borderRadius="full"
                                                            fontSize="sm"
                                                            fontWeight="bold"
                                                        >
                                                            {property.category}
                                                        </Badge>
                                                    )}
                                                </HStack>
                                                <Heading size="md" mb={2}>
                                                    {property.title}
                                                </Heading>
                                                {property.description && (
                                                    <Text fontSize="sm" mb={2} noOfLines={2}>
                                                        {property.description}
                                                    </Text>
                                                )}
                                                <Flex
                                                    align="center"
                                                    justify="space-between"
                                                    flexDir={{ base: "column", md: "row" }}
                                                    gap={2}
                                                >
                                                    {property.price && (
                                                        <Text fontSize="xl" fontWeight="bold">
                                                            {formatPrice(property.price, property.currency, property.type)}
                                                        </Text>
                                                    )}
                                                </Flex>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Navigation Buttons */}
                    <IconButton
                        icon={<ChevronLeftIcon boxSize={5} />}
                        position="absolute"
                        left={{ base: 2, md: 4 }}
                        top="50%"
                        transform="translateY(-50%)"
                        onClick={prevSlide}
                        bg="whiteAlpha.900"
                        _hover={{ bg: "white", transform: "translateY(-50%) scale(1.1)" }}
                        borderRadius="full"
                        size="md"
                        boxShadow="lg"
                        transition="all 0.3s"
                        aria-label="Previous slide"
                    />
                    <IconButton
                        icon={<ChevronRightIcon boxSize={5} />}
                        position="absolute"
                        right={{ base: 2, md: 4 }}
                        top="50%"
                        transform="translateY(-50%)"
                        onClick={nextSlide}
                        bg="whiteAlpha.900"
                        _hover={{ bg: "white", transform: "translateY(-50%) scale(1.1)" }}
                        borderRadius="full"
                        size="md"
                        boxShadow="lg"
                        transition="all 0.3s"
                        aria-label="Next slide"
                    />

                    {/* Dots Indicator */}
                    <Flex justify="center" mt={4} gap={2}>
                        {properties.map((_, index) => (
                            <Box
                                key={index}
                                as="button"
                                w={index === currentSlide ? 6 : 2}
                                h={2}
                                bg={index === currentSlide ? "cyan.500" : "gray.300"}
                                borderRadius="full"
                                transition="all 0.5s"
                                onClick={() => setCurrentSlide(index)}
                            />
                        ))}
                    </Flex>
                </Box>

                {/* Thumbnails */}
                <Flex mt={4} gap={3} overflowX="auto" justify="center">
                    {properties.map((property, index) => (
                        <Box
                            key={property.id}
                            cursor="pointer"
                            borderRadius="xl"
                            overflow="hidden"
                            border={index === currentSlide ? '2px solid cyan' : '2px solid transparent'}
                            opacity={index === currentSlide ? 1 : 0.6}
                            transition="all 0.3s"
                            _hover={{ opacity: 1, transform: 'translateY(-2px)' }}
                            onClick={() => setCurrentSlide(index)}
                            w="100px"
                            h="70px"
                            flexShrink={0}
                        >
                            <Box
                                as="img"
                                src={getPropertyImage(property)}
                                w="100%"
                                h="100%"
                                objectFit="cover"
                            />
                        </Box>
                    ))}
                </Flex>

            </Container>
        </Box>
    );
};

export default PropertiesSlideshow;